<script setup lang="ts">
import type { IconName } from "../Icon/icons";
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import VDropdown from "../Dropdown/VDropdown.vue";
import VDropdownItem from "../DropdownItem/VDropdownItem.vue";
import VToolbarTrigger from "./VToolbarTrigger.vue";

export interface VGroupByItem {
  value: string
  name: string
  icon?: IconName | null
}

const {
  items,
  defaultValue = undefined,
  tooltipText = undefined,
} = defineProps<{
  items: VGroupByItem[]
  defaultValue?: string
  tooltipText?: string
}>();

const modelValue = defineModel<string>({ required: true });

const { t } = useI18n();

const groupByTooltip = computed(() => tooltipText ?? t("base.groupBy.tooltip"));

const alreadyDefaultTooltip = computed(() => t("base.groupBy.already_default"));

const isAtDefault = computed(() => {
  if (defaultValue === undefined) {
    return true;
  }
  return modelValue.value === defaultValue;
});

const isGroupByActive = computed(() => {
  if (defaultValue === undefined) {
    return false;
  }
  return !isAtDefault.value;
});

const showReset = computed(() => defaultValue !== undefined);

function selectGroupBy(value: string) {
  if (modelValue.value === value) {
    return;
  }
  modelValue.value = value;
}

function onReset() {
  if (!showReset.value || isAtDefault.value || defaultValue === undefined) {
    return;
  }
  modelValue.value = defaultValue;
}
</script>

<template>
  <VDropdown
    theme="verbleif-popper"
    placement="bottom-start"
    class="shrink-0"
  >
    <template #popper="{ hide }">
      <div class="options min-w-[180px]">
        <VDropdownItem
          v-if="showReset"
          :close-on-click="!isAtDefault"
          :disabled="isAtDefault"
          :disabled-notice="alreadyDefaultTooltip"
          :text="t('base.groupBy.reset')"
          icon="undo"
          :hide="hide"
          @click="onReset"
        />
        <div
          v-if="showReset"
          class="mx-2 my-1.5 border-t border-gray-200 dark:border-dark-600"
          role="separator"
        />
        <VDropdownItem
          v-for="item in items"
          :key="item.value"
          close-on-click
          :text="item.name"
          :icon="item.icon"
          :selected="modelValue === item.value"
          selection="single"
          :hide="hide"
          @click="selectGroupBy(item.value)"
        />
      </div>
    </template>
    <VToolbarTrigger
      icon="group"
      :label="groupByTooltip"
      :active="isGroupByActive"
      has-popup
    />
  </VDropdown>
</template>
